import type { Analytics } from 'firebase/analytics';

// Google Analytics (GA4) for the web app, with two interchangeable backends:
//
//  1. Firebase Analytics, used when the full Firebase web config is present
//     (VITE_FIREBASE_API_KEY + VITE_FIREBASE_APP_ID).
//  2. gtag.js GA4, used when only a measurement id is available
//     (VITE_GA_MEASUREMENT_ID, e.g. G-RZLJLV2YR4).
//
// Firebase wins when both are set, so the two never double-count. With neither,
// analytics is a no-op and the SDKs are dropped from the bundle.

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

const firebaseEnabled = Boolean(firebaseConfig.apiKey && firebaseConfig.appId);
const gtagEnabled = !firebaseEnabled && Boolean(GA_MEASUREMENT_ID);

type Backend = 'firebase' | 'gtag' | null;
let backend: Backend = null;

// --- Firebase backend state ---
let firebaseAnalytics: Analytics | null = null;
let firebaseLogEvent: (typeof import('firebase/analytics'))['logEvent'] | null = null;

// --- gtag backend ---
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

export function analyticsEnabled(): boolean {
  return firebaseEnabled || gtagEnabled;
}

export async function initAnalytics(): Promise<void> {
  if (firebaseEnabled) {
    const [{ initializeApp }, { getAnalytics, isSupported, logEvent }] = await Promise.all([
      import('firebase/app'),
      import('firebase/analytics'),
    ]);
    if (!(await isSupported())) return;
    firebaseAnalytics = getAnalytics(initializeApp(firebaseConfig));
    firebaseLogEvent = logEvent;
    backend = 'firebase';
    return;
  }

  if (gtagEnabled) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    gtag('js', new Date());
    // We emit page_view manually on route changes, so disable the automatic one.
    gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
    backend = 'gtag';
  }
}

/** Log a GA4 page_view. No-op until analytics is initialized. */
export function trackPageView(path: string, title?: string): void {
  const params = {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  };
  if (backend === 'firebase' && firebaseAnalytics && firebaseLogEvent) {
    firebaseLogEvent(firebaseAnalytics, 'page_view', params);
  } else if (backend === 'gtag') {
    gtag('event', 'page_view', params);
  }
}

/** Log a named GA4 event with optional params. No-op until initialized. */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (backend === 'firebase' && firebaseAnalytics && firebaseLogEvent) {
    firebaseLogEvent(firebaseAnalytics, name, params);
  } else if (backend === 'gtag') {
    gtag('event', name, params ?? {});
  }
}
